/**
 * @fileoverview Shared types + labels for the Pre-Appointment pipeline
 * (MOR-882)
 *
 * Row/status/label types are derived straight from the Convex function
 * return types so the surface never drifts from the backend.
 */

import type { FunctionReturnType } from "convex/server";
import type { api } from "@cvx/_generated/api";
import type { AppointmentType } from "@cvx/jobs/schema";

export type PipelineJob = FunctionReturnType<
  typeof api.preAppointment.pipeline.listPipelineJobs
>[number];

export type PipelineStatus = FunctionReturnType<
  typeof api.preAppointment.statuses.listByOrg
>[number];

export type PipelineLabel = FunctionReturnType<
  typeof api.preAppointment.labels.listByOrg
>[number];

export type PipelineReferrer = FunctionReturnType<
  typeof api.preAppointment.pipeline.listReferrers
>[number];

/** Display labels for every appointment type (job type, e.g. CVL). */
export const APPOINTMENT_TYPE_LABELS: Record<AppointmentType, string> = {
  cvl: "CVL",
  mvl: "MVL",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "SBR",
  voluntary_administration: "Voluntary Administration",
  deed_of_company_arrangement: "DOCA",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

/** Stable option list for filters/pickers, in schema order. */
export const APPOINTMENT_TYPE_OPTIONS = Object.entries(
  APPOINTMENT_TYPE_LABELS,
).map(([value, label]) => ({ value, label }));

/** Pipeline group key helpers shared by the board and the list. */
export const UNSTAGED_KEY = "__unstaged__";
export const NO_REFERRER_KEY = "__no_referrer__";
export const NO_TYPE_KEY = "__no_type__";
